import React, { useState } from 'react';
import {
  TrendingUp, Users, Car, FileText, Calendar, Wrench, AlertCircle,
  DollarSign, Coins, History, MapPin, Settings, LogOut, FlaskConical, AlertTriangle, ChevronDown
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { isHiddenModule } from '../../../config/hiddenModules';

function NativeLanguageSelector() {
  const { lang, setLang } = useLanguage();

  return (
    <div className="mt-4 w-full bg-gray-50 rounded-lg p-1.5 border border-gray-200 flex items-center justify-between">
      <button 
        onClick={() => setLang('fr')}
        className={`flex-1 py-1.5 text-xs font-bold rounded transition ${
          lang === 'fr' 
            ? 'bg-white shadow-sm text-blue-600 border border-gray-200' 
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        Français
      </button>
      <button 
        onClick={() => setLang('ar')}
        className={`flex-1 py-1.5 text-xs font-bold rounded transition ${
          lang === 'ar' 
            ? 'bg-white shadow-sm text-blue-600 border border-gray-200' 
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        العربية
      </button>
    </div>
  );
}

export default function Sidebar({ currentPage, setCurrentPage, settings = {}, alertsCount, sidebarOpen, setSidebarOpen }) {
  const { t, lang } = useLanguage();
  const { logout, enabledModules, isStaff } = useAuth();

  // Mixed menu: single items + collapsible groups (group entries have children).
  const menu = [
    { id: 'dashboard', icon: TrendingUp },
    { id: 'clients', icon: Users },
    { id: 'rentals', icon: FileText, customLabel: { fr: 'Contrats', ar: 'العقود' } },
    { id: 'reservations', icon: Calendar },
    { id: 'calendar', icon: Calendar },
    {
      group: 'vehicles', icon: Car, label: { fr: 'Véhicules', ar: 'المركبات' },
      children: [
        { id: 'analytics', icon: TrendingUp, customLabel: { fr: 'Car Analytics', ar: 'تحليلات السيارات' } },
        { id: 'cars', icon: Car },
        { id: 'vidange', icon: Wrench, customLabel: { fr: 'Entretien / Vidange', ar: 'الصيانة / تغيير الزيت' } },
      ],
    },
    {
      group: 'finance', icon: Coins, label: { fr: 'Finance', ar: 'المالية' },
      children: [
        { id: 'finances', icon: Coins, customLabel: { fr: 'Dépenses & Paiements', ar: 'المصاريف والمدفوعات' } },
        { id: 'cheques', icon: FileText },
        { id: 'invoices', icon: DollarSign },
      ],
    },
    { id: 'mokhalafat', icon: AlertCircle },
    { id: 'staff', icon: Users },
    { id: 'gps', icon: MapPin },
    { id: 'history', icon: History },
    { id: 'blacklistcheck', icon: AlertTriangle, customLabel: { fr: 'Vérif. Liste Noire', ar: 'التحقق من القائمة السوداء' } },
    { id: 'referral', icon: Coins, customLabel: { fr: 'Parrainage', ar: 'الإحالة' } },
    { id: 'settings', icon: Settings },
  ];

  // Pages always available (not gated by the per-agency module list). Employees
  // (staff) are restricted strictly to their permitted modules — only the
  // dashboard is always on for them, so Settings/admin stay hidden unless granted.
  const ALWAYS_ON = isStaff
    ? ['dashboard']
    : ['dashboard', 'settings', 'analytics', 'referral', 'blacklistcheck'];
  const isVisible = (it) => {
    // Globally hidden modules never appear in any navigation (Main or Developer).
    if (isHiddenModule(it.id)) return false;
    // For agency owner/admin (not staff): if enabledModules is null or empty, show ALL modules
    if (!isStaff && (!enabledModules || (Array.isArray(enabledModules) && enabledModules.length === 0))) {
      return true;
    }
    return ALWAYS_ON.includes(it.id) || (Array.isArray(enabledModules) && enabledModules.includes(it.id));
  };

  // Collapsible groups (default open, remembered in localStorage).
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarGroups') || '{}'); } catch { return {}; }
  });
  // Default open so all menu items (cars, vidange, analytics, finances, cheques, invoices) are visible immediately.
  const isGroupOpen = (entry) => {
    if (openGroups[entry.group] !== undefined) return openGroups[entry.group];
    return true;
  };
  const toggleGroup = (g) => setOpenGroups(prev => {
    const next = { ...prev, [g]: !(prev[g] ?? true) };
    try { localStorage.setItem('sidebarGroups', JSON.stringify(next)); } catch (_) {}
    return next;
  });

  const navigateTo = (id) => {
    setCurrentPage(id);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const HamburgerIcon = (open) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="22" height="22" viewBox="0 0 24 24" 
      fill="none" stroke="currentColor" strokeWidth="2.5" 
      strokeLinecap="round" strokeLinejoin="round"
    >
      {open ? (
        <g>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </g>
      ) : (
        <g>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </g>
      )}
    </svg>
  );

  return (
    <>
      {/* MOBILE TOP BAR */}
      <header className="lg:hidden fixed top-0 start-0 end-0 bg-white border-b border-gray-200 z-40 flex items-center justify-between px-4 py-3 shadow-sm transition-colors duration-200">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {settings.logo ? (
            <img src={settings.logo} className="h-8 w-8 object-contain flex-shrink-0" alt="Logo" />
          ) : (
            <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
              <Car className="w-5 h-5 text-blue-600" />
            </div>
          )}
          <div className="min-w-0">
            <h2 className="font-bold text-gray-900 text-sm leading-tight truncate">{settings.companyName || 'CAR TRACK APP'}</h2>
            <p className="text-[10px] text-gray-500 leading-tight">{t(currentPage)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {alertsCount > 0 && (
            <button
              onClick={() => navigateTo('dashboard')}
              className="relative p-2 bg-red-50 rounded-lg"
            >
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="absolute -top-1 -end-1 flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white border border-white">
                {alertsCount}
              </span>
            </button>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            aria-label="Menu"
          >
            {HamburgerIcon(sidebarOpen)}
          </button>
        </div>
      </header>
      
      {/* DESKTOP TOGGLE BUTTON (shown when sidebar closed) */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="hidden lg:flex fixed top-4 start-4 z-30 items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg shadow-md transition"
          aria-label="Open menu"
          title="Ouvrir le menu"
        >
          {HamburgerIcon(false)}
          <span className="text-sm font-medium text-gray-700">Menu</span>
        </button>
      )}
      
      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* SIDEBAR */}
      <aside 
        className={`fixed start-0 top-0 h-screen w-64 bg-white border-e border-gray-200 flex flex-col z-50 transition-transform duration-300 ${
          sidebarOpen 
            ? 'translate-x-0' 
            : (lang === 'ar' ? 'translate-x-full' : '-translate-x-full')
        }`}
      >
        <div className="p-4 lg:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {settings.logo ? (
              <img src={settings.logo} className="h-10 w-10 object-contain" alt="Logo" />
            ) : (
              <div className="p-2 bg-blue-100 rounded-lg">
                <Car className="w-6 h-6 text-blue-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-gray-900 text-sm truncate">{settings.companyName || 'CAR TRACK APP'}</h2>
              <p className="text-xs text-gray-500">{t('car_rental')}</p>
            </div>
            
            {/* Close button */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
              title="Fermer le menu"
              aria-label="Close menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <NativeLanguageSelector />
        </div>
        
        <nav className="flex-1 p-3 overflow-y-auto">
          {(() => {
            const renderItem = (item, nested = false) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => navigateTo(item.id)}
                  className={`w-full flex items-center justify-between ${nested ? 'px-3 py-2.5' : 'px-3 py-3'} rounded-lg mb-1 text-sm transition ${
                    currentPage === item.id
                      ? 'bg-blue-50 text-blue-700 font-bold'
                      : 'text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    <span>{item.customLabel ? (item.customLabel[lang] || item.customLabel.fr) : t(item.id)}</span>
                  </div>
                  {item.id === 'dashboard' && alertsCount > 0 && (
                    <span className="flex h-5 min-w-5 px-1.5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-pulse shadow-sm shadow-red-500/50 border border-white">
                      {alertsCount}
                    </span>
                  )}
                </button>
              );
            };

            return menu.map(entry => {
              // Collapsible group
              if (entry.group) {
                const children = entry.children.filter(isVisible);
                if (children.length === 0) return null;
                const GIcon = entry.icon;
                const open = isGroupOpen(entry);
                const hasActive = children.some(c => c.id === currentPage);
                return (
                  <div key={entry.group} className="mb-1">
                    <button
                      onClick={() => toggleGroup(entry.group)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide transition ${
                        hasActive ? 'text-blue-700' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <GIcon className="w-5 h-5" />
                        <span>{entry.label[lang] || entry.label.fr}</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`} />
                    </button>
                    {open && (
                      <div className={`mt-0.5 ${lang === 'ar' ? 'mr-3 border-r pr-2' : 'ml-3 border-l pl-2'} border-gray-100`}>
                        {children.map(c => renderItem(c, true))}
                      </div>
                    )}
                  </div>
                );
              }
              // Single item
              if (!isVisible(entry)) return null;
              return renderItem(entry);
            });
          })()}
        </nav>
        
        <div className="p-3 border-t border-gray-200">
          <button 
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-bold text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {t('logout')}
          </button>
        </div>
      </aside>
    </>
  );
}
